/**
 * agentSurface — the AGENT tab helpers (agent wave, client leg).
 *
 * The Agents tab is the task queue GROUPED BY AGENT: every served queue task
 * row projects its assignment as `body.task.agent` (the world's assignment
 * map claim projects onto the task row — control/agent.py `_claim_assignment`);
 * tasks with no `task.agent` ARE the ambiguity lane and render as the
 * "Unassigned" group.
 *
 * A USER RE-ASSIGN is the SAME move as the auto map — a superseding keyed map
 * claim with human confidence. The client only DECLARES it: an announce on the
 * EXISTING channel ADD path whose ref lane carries the task→agent map atom +
 * the task family atom, with `{task, agent}` machine ids as evidence
 * (world-graph/world_graph/control/agent.py `reassignment_declared` /
 * `reassignment_declaration_rows` — mirrored EXACTLY; the ref lane must NOT
 * carry a queue-lane family atom or the world's queue-ADD branch would fire
 * first, control/data.py `_write`).
 *
 * Creating an agent from the client rides the same ADD path with the agent
 * lifecycle atom on the ref lane (the world-side mint hook is the parallel
 * orchestrator leg). UI language law: the UI says "Agent"; integration/
 * connector surfaces keep their own words.
 */

import { AGENT_HUMAN_LANE_REF } from "./feedRefs";
import { taskQueueLaneOfRefs, type TaskQueueLane } from "./taskAsk";

// Oracle atoms (graph.py) — the declared write contract, never a private shape.
export const TASK_AGENT_MAP_REF = "sci//task:@agent:@map**";
export const AGENT_MACHINE_REF = "sci//agent:@machine:@binary";
export const TASK_FAMILY_REF = "sci//task:@machine:@binary";

export const UNASSIGNED_GROUP_LABEL = "Unassigned";

type JsonRecord = Record<string, unknown>;
const isRecord = (value: unknown): value is JsonRecord => (
  typeof value === "object" && value !== null && !Array.isArray(value)
);

/** The assigned agent machine ids a served task row projects —
 * `raw.body.task.agent` is the task's agent LIST (sci//task:@agent:@map** is
 * PLURAL — never an either/or; [] = unassigned, the ambiguity lane). A
 * pre-list stored scalar read-tolerates as a one-element list. */
export const agentsOfTaskRow = (raw: unknown): string[] => {
  if (!isRecord(raw)) return [];
  const body = isRecord(raw.body) ? raw.body : {};
  const task = isRecord(body.task) ? body.task : {};
  if (Array.isArray(task.agent)) {
    return task.agent.map((a) => String(a || "").trim()).filter(Boolean);
  }
  return typeof task.agent === "string" && task.agent.trim() ? [task.agent.trim()] : [];
};

/** The task row's own machine id (`raw.body.machine.id`, e.g. task:queue:<sha>)
 * — the `task` half of the re-assign evidence. */
export const taskMachineIdOfRow = (raw: unknown): string => {
  if (!isRecord(raw)) return "";
  const body = isRecord(raw.body) ? raw.body : {};
  const machine = isRecord(body.machine) ? body.machine : {};
  return typeof machine.id === "string" ? machine.id.trim() : "";
};

/** The queue lane of a served task row: the DECLARED lane atom on its refs
 * first; else the row key's own scope declaration (an org-scoped protocol key
 * is the org queue by construction). */
export const taskQueueLaneOfRow = (raw: unknown): TaskQueueLane | null => {
  if (!isRecord(raw)) return null;
  const declared = taskQueueLaneOfRefs(raw.ref) ?? taskQueueLaneOfRefs(raw.refs);
  if (declared) return declared;
  const key = typeof raw.key === "string" ? raw.key : "";
  if (!key) return null;
  return key.includes("::org:") || key.startsWith("sci::org:") ? "org" : "sci";
};

/** Human words for an agent machine id — the UI never shows protocol
 * spellings ("harness-helper" → "Harness helper"). */
export const humanAgentLabel = (machineId: string): string => {
  const words = String(machineId || "")
    .replace(/^sci(::org:[^:]+)?::agent:/, "")
    .replace(/[:_./-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
};

/** Deterministic agent machine id from a typed human name — lowercase,
 * word-dashed ("Harness helper" → "harness-helper"). The world validates
 * existence/mint; the client only spells deterministically. */
export const agentMachineIdOfName = (name: string): string => String(name || "")
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

export type AgentTaskGroup<T> = { agent: string; label: string; items: T[] };

/** Group task items by their assigned agents (THE LIST LAW: a task rides
 * under EACH of its agents); unassigned last as its own group. Group order =
 * first appearance (the served feed order). */
export const groupTasksByAgent = <T,>(
  items: T[],
  agentsOf: (item: T) => string[],
): AgentTaskGroup<T>[] => {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const agents = agentsOf(item);
    for (const agent of agents.length ? agents : [""]) {
      const bucket = groups.get(agent);
      if (bucket) bucket.push(item);
      else groups.set(agent, [item]);
    }
  }
  const assigned = [...groups.entries()]
    .filter(([agent]) => agent !== "")
    .map(([agent, grouped]) => ({ agent, label: humanAgentLabel(agent), items: grouped }));
  const unassigned = groups.get("");
  return [
    ...assigned,
    ...(unassigned ? [{ agent: "", label: UNASSIGNED_GROUP_LABEL, items: unassigned }] : []),
  ];
};

/** The distinct known agents across the served rows — the re-assign picker's
 * choices (served data only; free entry covers the rest). */
export const knownAgentsOfRows = (raws: unknown[]): string[] => {
  const out = new Set<string>();
  for (const raw of raws) {
    for (const agent of agentsOfTaskRow(raw)) out.add(agent);
  }
  return [...out].sort();
};

const announceId = (head: string): string =>
  `${head}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

const naiveNow = (): string => new Date().toISOString().replace(/Z$/, "");

/** THE RE-ASSIGN ANNOUNCE — mirrors control/agent.py
 * `reassignment_declaration_rows`: ref lane = the task→agent map atom + the
 * task family atom (NEVER a queue-lane atom — the queue-ADD branch dispatches
 * first); evidence body.data = {task, agent} machine ids. Rides the queue's
 * own logical conversation like every task ask. */
export const reassignAnnounce = (opts: {
  taskMachineId: string;
  /** THE LIST LAW: the task's full picked agent list (the declaration is
   * authoritative — the world diffs it into per-edge claims). */
  agentMachineIds: string[];
  conversationId: string;
  taskLabel?: string;
}): {
  conversationId: string;
  timestamp: string;
  machine: Record<string, unknown>;
  ref: string[];
  artifact: unknown[];
  evidence: unknown[];
  confirmation: unknown[];
} => {
  const task = String(opts.taskMachineId || "").trim();
  const agents = (opts.agentMachineIds || []).map((a) => String(a || "").trim()).filter(Boolean);
  const conversationId = String(opts.conversationId || "").trim();
  if (!task) throw new Error("a re-assign requires the task machine id");
  if (!conversationId) throw new Error("a re-assign rides the queue's own conversation — conversationId is required");
  const id = announceId("agent-assign");
  const label = `Assign ${opts.taskLabel || "request"} to ${agents.map(humanAgentLabel).join(", ") || "no one"}`;
  return {
    conversationId,
    timestamp: naiveNow(),
    machine: { name: "message", id, label: "Assign agent", status: "start" },
    ref: [TASK_AGENT_MAP_REF, TASK_FAMILY_REF],
    artifact: [{
      machine: { name: "message", id, label: "Assign agent", tags: ["Message", "Agent"], status: "start" },
      suffix: "md",
      position: "match",
      input: { text: label },
      params: [],
    }],
    evidence: [{
      kind: "evidence",
      confidence: 1,
      body: { data: { task, agent: agents } },
    }],
    confirmation: [],
  };
};

/** THE CREATE-AGENT ANNOUNCE — the + menu's "New agent": the agent lifecycle
 * atom on the ref lane; evidence body.data mirrors the world mint hook
 * (`control/agent.py agent_declaration_rows`: `{name, label?, skill?}` —
 * the WORLD owns the machine token; the client never slugs an id). */
export const createAgentAnnounce = (opts: {
  name: string;
  skills?: string[];
  /** the display label to keep on an UPDATE (the agent card's skills editor
   * re-declares by machine token; without this the label drifts to the token). */
  label?: string;
  conversationId: string;
  /** PERSONAL (agent wave): true adds the human agent lane to the ref lane —
   * the outlet routes the announce to the BEARER's own human scope (mirrors
   * the harness `agent` command's A PERSONAL AGENT step EXACTLY). Company
   * (default) keeps the create announce unchanged. */
  personal?: boolean;
}): {
  conversationId: string;
  timestamp: string;
  machine: Record<string, unknown>;
  ref: string[];
  artifact: unknown[];
  evidence: unknown[];
  confirmation: unknown[];
} => {
  const name = String(opts.name || "").trim();
  if (!name) throw new Error("an agent requires a name");
  const conversationId = String(opts.conversationId || "").trim();
  if (!conversationId) throw new Error("creating an agent rides the channel ADD path — conversationId is required");
  const skills = (opts.skills || []).map((s) => String(s || "").trim()).filter(Boolean);
  const id = announceId("agent-create");
  const text = skills.length ? `New agent: ${name} — ${skills.join(", ")}` : `New agent: ${name}`;
  return {
    conversationId,
    timestamp: naiveNow(),
    machine: { name: "message", id, label: "New agent", status: "start" },
    ref: opts.personal ? [AGENT_MACHINE_REF, AGENT_HUMAN_LANE_REF] : [AGENT_MACHINE_REF],
    artifact: [{
      machine: { name: "message", id, label: "New agent", tags: ["Message", "Agent"], status: "start" },
      suffix: "md",
      position: "match",
      input: { text },
      params: [],
    }],
    evidence: [{
      kind: "evidence",
      confidence: 1,
      body: { data: { name, label: String(opts.label || "").trim() || name,
                      ...(skills.length ? { skill: skills } : {}) } },
    }],
    confirmation: [],
  };
};

// THE AGENT BOOK IS DEAD (feed wave): the client-built agent tab — the book
// read (`query {tab:"agents"}`), the scope toggle, and the agent list/card
// seeds — was deleted with the served-arrangement rail; the agent artifacts
// surface through the served Artifacts/Working tabs and the pins like every
// other artifact. The intent RELAY below stays: the stream/prioritized rail still
// transport the agent artifacts' in-artifact affordances.

/** THE AGENT ACTOR IS THE THING (Ori 2026-07-26) — not a UI template. The
 * Agent tab serves the `agent:*` rows, and an agent may wear any number of UI
 * templates (the registered card is only the default), so an intent NEVER
 * pins a template id. It lands on the conversation of the artifact it was
 * raised in — that artifact's own logical conversation (its machine id by
 * construction), exactly like the sibling task-ask router. The agent row is
 * walkable to its living artifact through `sci//agent:artifact:@artifact`;
 * nothing here guesses a machine. */
const originConversation = (
  a: { conversationId?: string },
  artifact: unknown,
): string => a.conversationId
  || String((artifact as { machine?: { id?: unknown } })?.machine?.id ?? "");

/** THE AGENT-INTENT ROUTING OWNER (the relay half beside
 * routeTaskAskInteraction): the agent-list artifact's in-artifact affordances
 * emit `submitted` interaction events the shell transports through the ONE
 * channel announce path — never a new transport:
 *   - re-assign `{submitted, task, agent}` → `reassignAnnounce` (the
 *     superseding human-confidence map claim, world-side);
 *   - create `{submitted, name, skill[]}` → `createAgentAnnounce` (the world
 *     owns the machine mint; `personal` adds the human lane per the active
 *     scope toggle).
 * Returns whether the interaction was an agent intent. `onRouted` fires only
 * after the announce SUCCEEDS (the re-download moment; never a poll). */
export const routeAgentIntent = (
  announce: (announcement: ReturnType<typeof reassignAnnounce>) => Promise<unknown>,
  a: { conversationId?: string },
  artifact: unknown,
  interaction: string,
  detail?: Record<string, unknown>,
  opts?: { personal?: boolean; onRouted?: () => void },
): boolean => {
  if (interaction !== "submitted" || !detail) return false;
  const task = typeof detail.task === "string" ? detail.task.trim() : "";
  const agent = typeof detail.agent === "string" ? detail.agent.trim() : "";
  const name = typeof detail.name === "string" ? detail.name.trim() : "";
  const query = typeof detail.query === "string" ? detail.query.trim() : "";
  if (query) return false; // an ask (born assigned or plain) — the task-ask owner routes it
  if (task && agent) {
    void announce(reassignAnnounce({
      taskMachineId: task,
      agentMachineIds: [agent],
      conversationId: originConversation(a, artifact),
    }))
      .then(() => opts?.onRouted?.())
      .catch((error) => console.error("[agent-assign] the re-assign announce failed (never reached the world):", error));
    return true;
  }
  if (name) {
    const skills = Array.isArray(detail.skill)
      ? detail.skill.filter((word): word is string => typeof word === "string" && !!word.trim())
      : [];
    void announce(createAgentAnnounce({
      name,
      skills,
      ...(typeof detail.label === "string" && detail.label.trim()
        ? { label: detail.label.trim() } : {}),
      conversationId: originConversation(a, artifact),
      ...(opts?.personal ? { personal: true } : {}),
    }))
      .then(() => opts?.onRouted?.())
      .catch((error) => console.error("[agent-create] the create announce failed (never reached the world):", error));
    return true;
  }
  return false;
};

/** The dehydrated seed of the org's usage-audit statement instance
 * (`audit_statement_usage_<orgId>`, claimed world-side at every era close —
 * control/audit.py `_recompose_audit_statements`). The real body hydrates
 * through the normal download path, exactly like the queue cards. */
export const auditUsageSeed = (orgId: string): Record<string, unknown> => ({
  machine: {
    id: `audit_statement_usage_${orgId}`,
    name: "audit-statement",
    label: "Usage audit",
  },
  suffix: "html",
  position: "interactive",
  params: [],
});
